import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isBrowser } from "react-device-detect";
import { Link } from "react-router-dom";

import {
  ColumnOneTableCellSkeleton,
  PrimaryButton,
  StepType,
  Table,
  TableCellSkeleton,
} from "../../components";
import { SharedSessionColumns } from "../../components/shared/SharedSessionColumns";
import { useModalContext } from "../../contexts/ModalContext";
import { usePlayerContext } from "../../contexts/PlayerContext";
import { useUIContext } from "../../contexts/UIContext";
import { usePlayerLoading } from "../../hooks/usePlayerLoading";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { playerSlice } from "../../redux/slices/playerSlice";
import {
  useGetPlayerByIdQuery,
  useGetSessionsByStatusQuery,
} from "../../services";
import {
  normalizeFetchError,
  uploadSessionStepList,
} from "../../shared/Constants";
import {
  enableNextButtonInModal,
  parseCompletedSessions,
  parseInProgressSessions,
  upsertPlayerById,
} from "../../shared/Functions";

export const Sessions: React.FC = (): JSX.Element => {
  // State and AppContext
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("homeDashboardPageSize") || "10",
  );
  const { setModalState, modalState } = useModalContext();
  const { playerIdToRetrieve, setPlayerIdToRetrieve } = usePlayerContext();
  const { setDisplayBack } = useUIContext();

  // Redux calls
  const uploadSessionFormValues = useAppSelector(
    (state) => state.uploadSessionForm,
  );
  const orgData = useAppSelector((state) => state.org);
  const pendingSessionCount = orgData.sessions.pending;

  // Loads players in parallel; reads from Redux (uses cache if already loaded)
  const { storedPlayerList, fetchingPlayers } = usePlayerLoading();

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storePlayers } = playerSlice.actions;

  // RTK Queries
  const {
    data: pendingSessions,
    isFetching: fetchingPending,
    isError: pendingError,
  } = useGetSessionsByStatusQuery(
    {
      status: "pending",
      limit: isBrowser ? 3 : pendingSessionCount,
      offset: 0,
    },
    { skip: pendingSessionCount === 0 },
  );
  const {
    data: processedSessions,
    isFetching: fetchingProcessed,
    isError: processedError,
  } = useGetSessionsByStatusQuery({
    status: "processed",
    limit: 7,
    offset: 0,
  });
  const { data: singlePlayer } = useGetPlayerByIdQuery(playerIdToRetrieve, {
    skip: !playerIdToRetrieve,
  });

  // Data processing and parsing
  const inProgressSessionList = useMemo(
    () => parseInProgressSessions(pendingSessions || []),
    [pendingSessions],
  );
  const completedSessionList = useMemo(
    () => parseCompletedSessions(processedSessions || []),
    [processedSessions],
  );
  const memoizedSharedSessionColumns = useMemo(SharedSessionColumns, []);

  const processingTableData = useMemo(
    () => (fetchingPending ? Array(3).fill({}) : inProgressSessionList),
    [fetchingPending, inProgressSessionList],
  );

  const processingTableColumns = useMemo(
    () =>
      fetchingPending
        ? memoizedSharedSessionColumns.map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : memoizedSharedSessionColumns,
    [fetchingPending, memoizedSharedSessionColumns],
  );

  const completedTableData = useMemo(
    () => (fetchingProcessed ? Array(7).fill({}) : completedSessionList),
    [fetchingProcessed, completedSessionList],
  );

  const completedTableColumns = useMemo(
    () =>
      fetchingProcessed
        ? memoizedSharedSessionColumns.map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : memoizedSharedSessionColumns,
    [fetchingProcessed, memoizedSharedSessionColumns],
  );

  const handleSinglePlayerUpdate = useCallback(() => {
    if (singlePlayer && storedPlayerList) {
      const updatedPlayerList = upsertPlayerById(
        storedPlayerList,
        singlePlayer,
      );
      dispatch(storePlayers(updatedPlayerList));
      setPlayerIdToRetrieve("");
    }
  }, [
    singlePlayer,
    storedPlayerList,
    dispatch,
    storePlayers,
    setPlayerIdToRetrieve,
  ]);

  useEffect(() => {
    handleSinglePlayerUpdate();
  }, [handleSinglePlayerUpdate]);

  const stepList: StepType[] = useMemo((): StepType[] => {
    if (orgData && uploadSessionFormValues) {
      return uploadSessionStepList({
        playerList: storedPlayerList,
        sessionFormValues: uploadSessionFormValues,
        orgData: orgData,
      });
    } else {
      return [];
    }
  }, [orgData, uploadSessionFormValues, storedPlayerList]);

  const openModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      shouldOpen: true,
      title: "Upload Data",
      steps: stepList,
    }));
  }, [setModalState, stepList]);

  const checkIfNextButtonIsEnabled = useCallback(() => {
    enableNextButtonInModal(modalState, setModalState, stepList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.steps, stepList]);

  useEffect(() => {
    setDisplayBack && setDisplayBack(false);
    checkIfNextButtonIsEnabled();
  }, [setDisplayBack, checkIfNextButtonIsEnabled]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="relative pb-20 z-0 overflow-y-auto w-full">
      <div
        className="hidden lg:block mt-8 px-4"
        data-testid="desktop-home-dashboard-container"
      >
        <div className="max-w-8xl mx-auto sm:flex sm:justify-between items-center sm:pb-2">
          <div className="flex justify-start items-center">
            <h2
              className="max-w-7xl sm:mt-4 px-0 text-2xl sm:text-3xl leading-6 font-bold text-gray-900 sm:px-6 lg:px-8 lg:pb-6"
              data-testid="sessions-processing-header"
            >
              Sessions Processing
            </h2>
            {fetchingPlayers ? (
              <p className="text-gray-400 cursor-not-allowed ">View All</p>
            ) : (
              <Link
                to="/processing-sessions"
                className="text-blue-800 hover:text-blue-600 "
                data-testid="sessions-processing-view-all-link"
              >
                View All
              </Link>
            )}
          </div>
          <PrimaryButton
            className="mr-8"
            onClick={() => openModal()}
            data-testid="upload-player-data-button"
            disabled={fetchingPlayers}
          >
            Upload Player Data
          </PrimaryButton>
        </div>
        <div data-testid="sessions-processing-table-container">
          <Table
            columns={processingTableColumns}
            data={processingTableData}
            data-testid="sessions-processing-table"
            selectedPageSize={selectedPageSize}
            setSelectedPageSize={setSelectedPageSize}
            displayFooter={false}
            fetching={fetchingPending}
            fetchError={pendingError ? normalizeFetchError() : null}
            customRowClassName="processing-sessions-row"
            hideSearch={true}
          />
        </div>
        <div className="max-w-8xl mx-auto justify-between items-center pb-2 pt-8">
          <div className="flex justify-start items-center">
            <h2
              className="mt-4 px-4 text-3xl leading-6 font-bold text-gray-900 sm:px-6 lg:px-8 lg:pb-6"
              data-testid="sessions-completed-header"
            >
              Sessions Completed
            </h2>
            <Link
              to="/completed-sessions"
              className="text-blue-800 hover:text-blue-600"
              data-testid="sessions-completed-view-all-link"
            >
              View All
            </Link>
          </div>
        </div>
        <div data-testid="sessions-completed-table-container">
          <Table
            columns={completedTableColumns}
            data={completedTableData}
            data-testid="sessions-completed-table"
            selectedPageSize={selectedPageSize}
            setSelectedPageSize={setSelectedPageSize}
            displayFooter={false}
            fetching={fetchingProcessed}
            fetchError={processedError ? normalizeFetchError() : null}
            customRowClassName="completed-sessions-row"
            hideSearch={true}
          />
        </div>
      </div>
    </main>
  );
};
